import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';
import {
  ISaveSpecialConvertInstructionRequest,
  ISpecialConvertInstruction,
} from './models/special-convert-instruction.model';

/**
 * Обёртка над SI-эндпоинтами IikoWebHookController.
 * JWT навешивает TokenInterceptorService — запросы идут на environment.baseUrl.
 */
@Injectable()
export class SpecialInstructionsApiService {
  private readonly url = `${environment.baseUrl}/api/external-systems/iiko/special-convert-instructions`;

  constructor(private http: HttpClient) {
  }

  /**
   * Инструкций может не быть — бэк отвечает 200 с пустым телом, HttpClient отдаёт null.
   * Это не ошибка: открываем пустой редактор.
   */
  public get(organizationId: string): Observable<ISpecialConvertInstruction | null> {
    return this.http.get<ISpecialConvertInstruction | null>(`${this.url}/${organizationId}`);
  }

  /** Полная перезапись: бэк не мержит, отправляем всё содержимое экрана целиком. */
  public save(organizationId: string, specialConvertInstruction: ISpecialConvertInstruction): Observable<void> {
    const request: ISaveSpecialConvertInstructionRequest = {organizationId, specialConvertInstruction};

    return this.http.post<void>(this.url, request);
  }
}

/** Тело ошибки может прийти как text/plain — тогда HttpClient кладёт строку в error.error. */
export function extractServerMessage(error: HttpErrorResponse): string | null {
  if (typeof error?.error === 'string') {
    return error.error;
  }

  return error?.error?.message ?? error?.error?.errors?.[0]?.message ?? null;
}

/**
 * 403 на этих эндпоинтах приходит из FilterAggregatorMiddleware: агрегатор iiko выключен
 * для организации. Отдельное сообщение, потому что «нет доступа» тут означает не права,
 * а выключенную интеграцию.
 */
export function describeSpecialInstructionsError(error: HttpErrorResponse, translate: TranslateService): string {
  if (error?.status === 403) {
    return translate.instant('MENU.SPECIAL_INSTRUCTIONS.ERRORS.FORBIDDEN');
  }

  if (error?.status === 0) {
    return translate.instant('MENU.SPECIAL_INSTRUCTIONS.ERRORS.NETWORK');
  }

  return extractServerMessage(error) || translate.instant('COMMON.SOME_ERROR');
}
